/**
 * API Client - Axios 封装
 */

import { trackApiEvent } from '../utils/analytics'
import WebApp from '@twa-dev/sdk'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: any
  params?: Record<string, string | number | undefined>
}

export async function api<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params } = options
  const startedAt = performance.now()

  // 构建URL
  let url = `${API_BASE_URL}${endpoint}`
  
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  // 发送请求
  let response: Response
  try {
    const initData = WebApp.initData
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(initData ? { 'X-TG-Init-Data': initData } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch (error) {
    trackApiEvent('network_error', {
      endpoint,
      method,
      duration_ms: Math.round(performance.now() - startedAt),
      error_message: error instanceof Error ? error.message : String(error),
    })
    throw error
  }

  const durationMs = Math.round(performance.now() - startedAt)

  if (durationMs > 5000) {
    trackApiEvent('slow_request', {
      endpoint,
      method,
      status: response.status,
      duration_ms: durationMs,
    })
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    trackApiEvent('api_error', {
      endpoint,
      method,
      status: response.status,
      duration_ms: durationMs,
      error_message: error.detail || `HTTP ${response.status}`,
    })
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

// 便捷方法
export const apiClient = {
  get: <T>(endpoint: string, params?: Record<string, string | number | undefined>) =>
    api<T>(endpoint, { method: 'GET', params }),
  
  post: <T>(endpoint: string, body: any) =>
    api<T>(endpoint, { method: 'POST', body }),
  
  put: <T>(endpoint: string, body: any) =>
    api<T>(endpoint, { method: 'PUT', body }),
  
  delete: <T>(endpoint: string, body?: any) =>
    api<T>(endpoint, { method: 'DELETE', body }),
}
